import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Alert, AlertDescription } from '../../ui/alert';
import { 
  Activity, 
  Users, 
  Clock, 
  Coffee,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  BarChart3,
  Calendar,
  MapPin,
  Briefcase,
  Timer,
  UserCheck,
  PauseCircle,
  PlayCircle,
  Building2,
  Tv,
  TrendingDown
} from 'lucide-react';
import { WorkEntry, User, Team, Project, Location } from '../../../types';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import TVRealTimeDashboard from '../../TVRealTimeDashboard';
import { ActiveWorkersManagement } from '../ActiveWorkersManagement';

interface DashboardOverviewTabProps {
  workEntries: WorkEntry[];
  allUsers: User[];
  teams: Team[];
  projects: Project[];
  locations: Location[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  isLoading: boolean;
  onRefresh: () => void;
}

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];

export const DashboardOverviewTab: React.FC<DashboardOverviewTabProps> = ({
  workEntries,
  allUsers,
  teams,
  projects,
  locations,
  selectedDate,
  onDateChange,
  isLoading,
  onRefresh
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showTVMode, setShowTVMode] = useState(false);
  const [overtimeData, setOvertimeData] = useState<any>(null);
  const [overtimeLoading, setOvertimeLoading] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch overtime data
  useEffect(() => {
    const fetchOvertimeData = async () => {
      setOvertimeLoading(true);
      try {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api';
        const response = await fetch(`${API_BASE_URL}/overtimes/summary?date=${selectedDate}`);
        if (response.ok) {
          const data = await response.json();
          setOvertimeData(data);
        }
      } catch (error) {
        console.error('Error fetching overtime data:', error);
      } finally {
        setOvertimeLoading(false);
      }
    };

    fetchOvertimeData();
  }, [selectedDate]);

  // Calculate real-time metrics
  const metrics = useMemo(() => {
    const activeEntries = workEntries.filter(e => e.isActive || (!e.endTime && e.startTime));
    const workingNow = activeEntries.filter(e => !e.activeBreak);
    const onBreakNow = activeEntries.filter(e => e.activeBreak);
    
    // Calculate productivity by team
    const teamProductivity = teams.map(team => {
      const teamMembers = allUsers.filter(u => u.teamId === team.id && u.role === 'worker');
      const teamActiveEntries = activeEntries.filter(e => 
        teamMembers.some(m => m.id === e.userId)
      );
      const productivity = teamMembers.length > 0 
        ? (teamActiveEntries.length / teamMembers.length) * 100 
        : 0;
      
      return {
        name: team.name,
        productivity,
        activeWorkers: teamActiveEntries.length,
        totalMembers: teamMembers.length
      };
    });

    // Calculate project distribution
    const projectDistribution = projects.map(project => {
      const projectEntries = workEntries.filter(e => e.projectId === project.id);
      const totalHours = projectEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : currentTime;
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);
      
      return {
        name: project.name,
        value: totalHours,
        count: projectEntries.length
      };
    }).filter(p => p.value > 0);

    // Hourly activity for today
    const hourlyActivity = Array.from({ length: 24 }, (_, hour) => {
      const hourStart = new Date(selectedDate);
      hourStart.setHours(hour, 0, 0, 0);
      const hourEnd = new Date(selectedDate);
      hourEnd.setHours(hour + 1, 0, 0, 0);
      
      const activeInHour = workEntries.filter(entry => {
        if (!entry.startTime) return false;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : currentTime;
        return start <= hourEnd && end >= hourStart;
      }).length;
      
      return {
        hour: `${hour}:00`,
        active: activeInHour
      };
    });

    // Alerts with proper defaults (8h work + 1h break)
    const alerts = [];
    activeEntries.forEach(entry => {
      if (entry.startTime) {
        const workMinutes = Math.floor((currentTime.getTime() - new Date(entry.startTime).getTime()) / (1000 * 60)) - (entry.totalBreakMinutes || 0);
        const user = allUsers.find(u => u.id === entry.userId);
        
        // Use user settings or defaults: 8h work (480 min) + 1h break (60 min)
        const userWorkQuotaMinutes = user?.dailyWorkQuotaMinutes || 480; // Default 8 hours
        const userBreakQuotaMinutes = user?.dailyBreakQuotaMinutes || 60; // Default 1 hour
        
        // Calculate total time at work
        const totalElapsedMinutes = Math.floor((currentTime.getTime() - new Date(entry.startTime).getTime()) / (1000 * 60));
        
        // Calculate total expected time (work quota + break quota) - e.g., 9h for 8h work + 1h break
        const totalExpectedMinutes = userWorkQuotaMinutes + userBreakQuotaMinutes;
        
        // Alert if total time at work exceeds the expected work + break time
        if (totalElapsedMinutes > totalExpectedMinutes) {
          const overtimeMinutes = totalElapsedMinutes - totalExpectedMinutes;
          alerts.push({
            type: 'overtime',
            severity: 'high',
            message: `${entry.userName} în ore suplimentare: +${(overtimeMinutes/60).toFixed(1)}h (total: ${(totalElapsedMinutes/60).toFixed(1)}h, normă: ${totalExpectedMinutes/60}h)`,
            userId: entry.userId
          });
        }
        
        // Alert if break time exceeds the allowed break quota
        if ((entry.totalBreakMinutes || 0) > userBreakQuotaMinutes) {
          alerts.push({
            type: 'break',
            severity: 'medium',
            message: `${entry.userName} a depășit timpul de pauză alocat de ${userBreakQuotaMinutes} minute (pauză totală: ${entry.totalBreakMinutes} min)`,
            userId: entry.userId
          });
        }
        
        // Additional alert if total time at work exceeds reasonable limits (e.g., 13 hours)
        if (totalElapsedMinutes > 780) { // 13 hours total
          alerts.push({
            type: 'excessive',
            severity: 'high',
            message: `${entry.userName} este la lucru de peste 13 ore!`,
            userId: entry.userId
          });
        }
      }
    });

    return {
      workingNow: workingNow.length,
      onBreakNow: onBreakNow.length,
      totalActiveToday: new Set(workEntries.map(e => e.userId)).size,
      teamProductivity,
      projectDistribution,
      hourlyActivity,
      alerts,
      avgProductivity: teams.length > 0 
        ? teamProductivity.reduce((sum, t) => sum + t.productivity, 0) / teams.length 
        : 0
    };
  }, [workEntries, allUsers, teams, projects, currentTime, selectedDate]);

  // TV Mode
  if (showTVMode) {
    return (
      <div>
        <Button
          onClick={() => setShowTVMode(false)}
          className="mb-4 flex items-center gap-2"
          variant="outline"
        >
          <Eye className="h-4 w-4" />
          Înapoi la Dashboard
        </Button>
        <TVRealTimeDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Real-time Clock */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Monitorizare în Timp Real</h2>
          <p className="text-gray-600">
            {format(currentTime, 'EEEE, d MMMM yyyy - HH:mm:ss', { locale: ro })}
          </p>
        </div>
        <Button
          onClick={() => setShowTVMode(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Tv className="h-4 w-4" />
          Mod TV
        </Button>
      </div>

      {/* Real-time Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Lucrează Acum</p>
                <p className="text-3xl font-bold text-green-700">{metrics.workingNow}</p>
                <div className="flex items-center gap-1 mt-2">
                  <PlayCircle className="h-4 w-4 text-green-600 animate-pulse" />
                  <span className="text-xs text-gray-600">Live</span>
                </div>
              </div>
              <Activity className="h-10 w-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">În Pauză</p>
                <p className="text-3xl font-bold text-orange-700">{metrics.onBreakNow}</p>
                <div className="flex items-center gap-1 mt-2">
                  <PauseCircle className="h-4 w-4 text-orange-600 animate-pulse" />
                  <span className="text-xs text-gray-600">Live</span>
                </div>
              </div>
              <Coffee className="h-10 w-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Activi Azi</p>
                <p className="text-3xl font-bold text-blue-700">{metrics.totalActiveToday}</p>
                <Progress value={metrics.avgProductivity} className="mt-2 h-2" />
              </div>
              <UserCheck className="h-10 w-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-50 to-pink-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alerte Active</p>
                <p className="text-3xl font-bold text-red-700">{metrics.alerts.length}</p>
                <div className="flex items-center gap-1 mt-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 animate-pulse" />
                  <span className="text-xs text-gray-600">Atenție</span>
                </div>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {metrics.alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Alerte Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.alerts.map((alert, index) => (
                <Alert key={index} className={alert.severity === 'high' ? 'border-red-500' : 'border-orange-500'}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>{alert.message}</span>
                    <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>
                      {alert.type === 'overtime' ? 'Ore Suplimentare' : 
                       alert.type === 'excessive' ? 'Timp Excesiv' : 
                       'Pauză Extinsă'}
                    </Badge>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overtime Section */}
      {overtimeData && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5 text-purple-600" />
              Ore Suplimentare - {format(new Date(selectedDate), 'd MMMM yyyy', { locale: ro })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600">Total Ore Suplimentare</p>
                <p className="text-2xl font-bold text-purple-700">{overtimeData.totalOvertimeHours}h</p>
                <p className="text-xs text-gray-500">{overtimeData.totalOvertimeMinutes} minute</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600">Muncitori cu Ore Suplimentare</p>
                <p className="text-2xl font-bold text-purple-700">{overtimeData.workersWithOvertime}</p>
                <p className="text-xs text-gray-500">persoane</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600">Închideri Automate</p>
                <p className="text-2xl font-bold text-purple-700">{overtimeData.autoGeneratedCount}</p>
                <p className="text-xs text-gray-500">sesiuni auto-închise</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-sm text-gray-600">Medie pe Persoană</p>
                <p className="text-2xl font-bold text-purple-700">
                  {overtimeData.workersWithOvertime > 0 
                    ? (overtimeData.totalOvertimeHours / overtimeData.workersWithOvertime).toFixed(1) 
                    : 0}h
                </p>
                <p className="text-xs text-gray-500">ore suplimentare</p>
              </div>
            </div>

            {/* Top Workers with Overtime */}
            {overtimeData.topWorkers && overtimeData.topWorkers.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold mb-2 text-sm text-gray-700">Top Muncitori cu Ore Suplimentare</h4>
                <div className="space-y-2">
                  {overtimeData.topWorkers.map((worker: any, index: number) => {
                    const user = allUsers.find(u => u.id === worker.userId);
                    const dailyQuotaHours = user ? (user.dailyWorkQuotaMinutes / 60).toFixed(1) : '8.0';
                    
                    return (
                      <div key={worker.userId} className="flex items-center justify-between bg-white rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            #{index + 1}
                          </Badge>
                          <span className="font-medium">{worker.userName}</span>
                          <span className="text-xs text-gray-500">
                            (normă {dailyQuotaHours}h/zi)
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">{worker.count} {worker.count === 1 ? 'sesiune' : 'sesiuni'}</span>
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                            {worker.totalHours}h overtime
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No overtime message */}
            {(!overtimeData.topWorkers || overtimeData.topWorkers.length === 0) && (
              <div className="text-center py-4 text-gray-500">
                <Timer className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>Nu există ore suplimentare înregistrate pentru această zi</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Workers Management Section */}
      <ActiveWorkersManagement 
        workEntries={workEntries}
        allUsers={allUsers}
        onRefresh={onRefresh}
      />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Productivity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Productivitate Echipe
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metrics.teamProductivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'productivity') return [`${value.toFixed(1)}%`, 'Productivitate'];
                    return [value, name];
                  }}
                />
                <Bar dataKey="productivity" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                  {metrics.teamProductivity.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.productivity >= 80 ? '#10b981' : entry.productivity >= 50 ? '#f59e0b' : '#ef4444'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Project Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Distribuție Proiecte (Ore)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metrics.projectDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value.toFixed(1)}h`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {metrics.projectDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activitate pe Ore - {format(new Date(selectedDate), 'd MMMM yyyy', { locale: ro })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={metrics.hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="active" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Team Status Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Status Echipe în Timp Real
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {metrics.teamProductivity.map((team) => (
              <div key={team.name} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">{team.name}</h4>
                  <Badge 
                    variant={team.productivity >= 80 ? 'success' : team.productivity >= 50 ? 'warning' : 'destructive'}
                  >
                    {team.productivity.toFixed(0)}%
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Membri activi:</span>
                    <span className="font-medium">{team.activeWorkers}/{team.totalMembers}</span>
                  </div>
                  
                  <Progress value={team.productivity} className="h-2" />
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {team.productivity >= 80 ? (
                      <>
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span>Performanță excelentă</span>
                      </>
                    ) : team.productivity >= 50 ? (
                      <>
                        <AlertTriangle className="h-3 w-3 text-yellow-500" />
                        <span>Necesită atenție</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3 text-red-500" />
                        <span>Productivitate scăzută</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};